// readAudit.mjs — /admin/read-audit 的纯逻辑（SB-116，第一百二十一批）。
//
// ── 这一页回答的问题 ────────────────────────────────────────────────────────
// 「谁读了这个资源」。访问控制回答"谁能读"，这一页回答"谁读了"——第一百零八批
// 那次排查（外包读没读过组间线）之所以答不出，就是因为读面只有控制没有审计。
// 后端的表与查询端点齐了之后，这一页是那本台账**唯一**的人类读法：
// 审计存了但查不到，等于没存。
//
// ── 为什么这些判断必须是纯函数 ──────────────────────────────────────────────
// 这一页的失效方式与运行日志页同型：打开了、但看到的是错的——
//   · 时间窗解析错一个时区，"那晚谁读过"查的就是另一个晚上，页面只会显示空白，
//     读起来像"没人读过"——对一页审计而言，这是最贵的一种假话；
//   · 一行 verdict 认不出来就悄悄丢掉，名单上恰好少的就是被拒的那次试探。
// 所以判断全部落在这里逐条钉测；页面只渲染（SB-042：`.jsx` 里不放判据）。
//
// 时间输入复用 adminLogs 的 localInputToMs——datetime-local 的"少一个 Z 差八小时"
// 只该有一份修法，抄一份就是第二个会漂的时区约定。
import { localInputToMs } from './adminLogs.mjs';

// 与后端 routes_admin.cpp 的钳位一致（默认 100、上限 500）。页面不做翻页：
// 一个资源在一个时间窗内的读取记录以百计已属异常，异常该收窄窗口而不是翻页。
// ⚠ SB-159（第三百八十二批）：**不翻页，但撞上上限时必须说出来**。热资源（group-threads 每读
// 一次线表就落一行）很快填满一页，而顶部那句「有哪些人读过」是从这一页算的——比这一页更早的
// 读者会静静不在名单上。服务端给 hasMore 与这一页最旧一行的时刻 oldestAtMs，页面据它指路
// 「把窗口收到那之前」，并把两份名单标成「仅本页」。
export const READ_AUDIT_LIMIT_DEFAULT = 100;
export const READ_AUDIT_LIMIT_MAX = 500;

// 撞上限了吗。只认严格的 hasMore === true（旧后端缺键 ⇒ null，不瞎报）。
export function readAuditLimitNote(data) {
  if (data?.hasMore !== true) return null;
  return { n: data.limit ?? null, oldestAtMs: Number.isFinite(data.oldestAtMs) ? data.oldestAtMs : null };
}

// 过滤器 → /api/admin/read-audit 的参数对象。resource 必填（trim 后非空才算有），
// 空值一律**不发**（与 buildLogQuery 同一条纪律）。
export function buildReadAuditQuery(f = {}) {
  const q = {};
  const resource = String(f.resource ?? '').trim();
  if (resource) q.resource = resource;
  const from = localInputToMs(f.from);
  const to = localInputToMs(f.to);
  if (from > 0) q.fromMs = from;
  if (to > 0) q.toMs = to;
  const limit = Number(f.limit);
  q.limit = Number.isFinite(limit) && limit > 0
    ? Math.min(Math.floor(limit), READ_AUDIT_LIMIT_MAX)
    : READ_AUDIT_LIMIT_DEFAULT;
  return q;
}

// 本地就能判定的错，不必往返服务端：资源标识缺席、起止写反。
// 返回 i18n 键（文案归词典，判据归这里）。
export function filterProblems(f = {}) {
  const out = [];
  if (!String(f.resource ?? '').trim()) out.push('admin.readAudit.err.resource');
  const from = localInputToMs(f.from);
  const to = localInputToMs(f.to);
  if (from > 0 && to > 0 && to < from) out.push('admin.readAudit.err.range');
  return out;
}

// ── 行归一化 ──────────────────────────────────────────────────────────────────
// 三条纪律：
//  · **一行都不丢**：这是审计。一行 verdict/credential 认不出来（后端 CHECK 之下
//    不该发生，但防的就是"不该发生"），保留原串交给页面渲染成原文，绝不静默剔除
//    ——名单上悄悄少一行，比页面上多一个奇怪的词坏得多。
//  · **不排序**：顺序（at_ms DESC，新的在前）是后端的展示语义，前端再排一次就是
//    第二个会漂的顺序。
//  · atMs 缺席 ⇒ null（`Number(null)` 是 0，0 会渲染成 1970-01-01——一句假话）。
export function normalizeReadAudit(data) {
  const rows = Array.isArray(data?.entries) ? data.entries : [];
  const out = [];
  for (const r of rows) {
    if (!r || typeof r !== 'object' || r.id == null) continue;
    out.push({
      id: String(r.id),
      accountId: r.accountId == null ? null : Number(r.accountId),
      email: typeof r.email === 'string' ? r.email : '',
      displayName: typeof r.displayName === 'string' ? r.displayName : '',
      credential: typeof r.credential === 'string' ? r.credential : '',
      surface: typeof r.surface === 'string' ? r.surface : '',
      resource: typeof r.resource === 'string' ? r.resource : '',
      verdict: typeof r.verdict === 'string' ? r.verdict : '',
      atMs: msOrNull(r.atMs),
    });
  }
  return out;
}

// 一行的读者标签：昵称 → 邮箱 → #id。审计行比账号长寿（append-only + 只按时间
// 清理），账号删了标签也得能落在**某个**可指认的东西上，绝不能空着——
// 空标签在名单里读起来像"没有人"。
export function readerLabel(row) {
  if (row?.displayName) return row.displayName;
  if (row?.email) return row.email;
  return row?.accountId == null ? '#?' : `#${row.accountId}`;
}

// 值域内的 verdict → i18n 键；值域外 ⇒ null（页面渲染原串，不硬套一个标签——
// 把认不出的词染成"放行"或"拒绝"都是在替台账说话）。
export function verdictKey(verdict) {
  if (verdict === 'allowed') return 'admin.readAudit.verdict.allowed';
  if (verdict === 'denied') return 'admin.readAudit.verdict.denied';
  return null;
}

export function credentialKey(credential) {
  if (credential === 'session') return 'admin.readAudit.cred.session';
  if (credential === 'key') return 'admin.readAudit.cred.key';
  return null;
}

// ── 读者名单（验收 §3 ② 的呈现半边）─────────────────────────────────────────
// 「给定时间窗 + 一条线 ⇒ 唯一答出读者名单」。**只数 allowed**：denied 是"谁在试"，
// 不是"谁读了"——把两者揉进一份名单，名单就又答不了"内容到底给谁看过"这个问题。
// 去重按账号（同一人读十次是一个读者），顺序保留首次出现（即最近一次在前）。
export function distinctReaders(rows) {
  const seen = new Set();
  const out = [];
  for (const r of rows ?? []) {
    if (r?.verdict !== 'allowed') continue;
    const key = r.accountId == null ? readerLabel(r) : String(r.accountId);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(readerLabel(r));
  }
  return out;
}

// 试探名单：denied 的那一列（"谁在试"同样是情报——被拒的那次也在台账上）。
export function distinctDeniedProbers(rows) {
  const seen = new Set();
  const out = [];
  for (const r of rows ?? []) {
    if (r?.verdict !== 'denied') continue;
    const key = r.accountId == null ? readerLabel(r) : String(r.accountId);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(readerLabel(r));
  }
  return out;
}

function msOrNull(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
